/**
 * Configuration section submenu — single source of truth for routes/labels
 * (used by Sidebar and pages like Document Categories for header tabs).
 */
export interface ConfigurationNavItem {
  page: string;
  labelKey: string;
  href: string;
  /** When true the Sidebar shows this entry to admin groups only, whatever the per-page
   *  permission says — for screens whose endpoints refuse non-admins anyway. */
  adminOnly?: boolean;
  /** Permission page the Sidebar gates this entry on. Without it the entry is shown to
   *  everyone and the page itself does the refusing — fine for a lookup screen, wrong
   *  for one whose endpoints are guarded by a page of their own. */
  permissionPage?: string | string[];
}

export const CONFIGURATION_SECTION_NAV: ConfigurationNavItem[] = [
  // { page: 'configuration-dashboard', labelKey: 'Sidebar.Dashboard', href: '/configuration/dashboard' },
  // Feeds the "Category" select on Document upload/edit.
  { page: 'document-categories', labelKey: 'Sidebar.DocumentCategories', href: '/document-categories', permissionPage: 'Settings' },
  // { page: 'document-approval', labelKey: 'Sidebar.DocumentApproval', href: '/document-approval' },
  // { page: 'services-status', labelKey: 'Sidebar.ServicesStatus', href: '/configuration/services-status' },
  // { page: 'Activities', labelKey: 'Sidebar.Activities', href: '/activities' },
  // { page: 'ControlReferencePage', labelKey: 'Sidebar.ControlReference', href: '/controlReference' },
  // { page: 'FinancialImpactsPage', labelKey: 'Sidebar.Financial Impacts', href: '/financialImpacts' },
  // { page: 'EventTypePage', labelKey: 'Sidebar.EventType', href: '/eventType' },
  // { page: 'AccountTypePage', labelKey: 'Sidebar.AccountType', href: '/accountType' },
  // { page: 'CreateSubBusinessUnitPage', labelKey: 'Sidebar.SubBusinessUnit', href: '/subBusinessUnit' },
  // { page: 'CreateSystem', labelKey: 'Sidebar.System', href: '/system' },
  // { page: 'CreateJobTitle', labelKey: 'Sidebar.JobTitle', href: '/jobTitle' },
  // { page: 'CreateDiscoveredType', labelKey: 'Sidebar.DiscoveredType', href: '/discoveredTypes' },
  // { page: 'CreateCurrencyPage', labelKey: 'Sidebar.Currency', href: '/createCurrency' },
  // { page: 'CreateRootCause', labelKey: 'Sidebar.RootCause', href: '/rootCause' },
  // { page: 'CreateSubFunction', labelKey: 'Sidebar.SubFunction', href: '/subFunction' },
  // { page: 'CreateRiskCategory', labelKey: 'Sidebar.RiskCategory', href: '/riskCategory' },
  // { page: 'collection-sops', labelKey: 'Sidebar.CollectionSops', href: '/catalogs/collection-sops' },
  // { page: 'sop-masters', labelKey: 'Sidebar.SopMasters', href: '/catalogs/sop-masters' },
  // { page: 'lifecycle-stages', labelKey: 'Sidebar.LifecycleStages', href: '/configuration/lifecycle-stages' },
  // { page: 'criticalities', labelKey: 'Sidebar.Criticalities', href: '/configuration/criticalities' },
  { page: 'asset-types', labelKey: 'Sidebar.AssetTypes', href: '/configuration/asset-types', permissionPage: 'Settings' },
  // Resource Classifications — the classification list used by Resources.
  { page: 'asset-classifications', labelKey: 'Sidebar.AssetClassifications', href: '/configuration/asset-classifications', permissionPage: 'Settings' },
  // Feeds the "Data Handled" picker on the resource form.
  { page: 'data-handled', labelKey: 'Sidebar.DataHandled', href: '/configuration/data-handled', permissionPage: 'Settings' },
  // The service-form lookups. Like every list here, changed under 'Settings'.
  { page: 'services-status', labelKey: 'Sidebar.ServicesStatus', href: '/configuration/services-status', permissionPage: 'Settings' },
  { page: 'lifecycle-stages', labelKey: 'Sidebar.LifecycleStages', href: '/configuration/lifecycle-stages', permissionPage: 'Settings' },
  { page: 'criticalities', labelKey: 'Sidebar.Criticalities', href: '/configuration/criticalities', permissionPage: 'Settings' },
  // { page: 'function', labelKey: 'Sidebar.RolesAndFunctions', href: '/function' },
  // { page: 'policy-categories', labelKey: 'Sidebar.PolicyCategories', href: '/policy/categories' },
  // { page: 'policy-settings', labelKey: 'Sidebar.Settings', href: '/policy/settings' },
  // { page: 'llm-routing', labelKey: 'Sidebar.LlmRouting', href: '/llm-routing' },
  // Gated on 'Settings' — the same page Function.tsx checks, so the menu entry and the
  // screen agree instead of offering a link that blocks on arrival.
  { page: 'function', labelKey: 'Sidebar.RolesAndFunctions', href: '/function', permissionPage: 'Settings' },
  { page: 'impact-area', labelKey: 'Sidebar.ImpactArea', href: '/impactArea', permissionPage: 'Settings' },
  // Feeds the single "Reference" select on Control create/edit.
  { page: 'references', labelKey: 'Sidebar.References', href: '/configuration/references' },

  // Removed from the menu: the read-only Tenable health view. Its connection checks now
  // live on the Tenable Settings screen below, next to the values they test. The page
  // component and its route remain, so restoring it is uncommenting this line.
  // { page: 'tenable-integration', labelKey: 'Sidebar.TenableIntegration', href: '/configuration/tenable' },
  // Connection settings for Tenable and Laserfiche — both on one screen, one entry.
  // Admin-only, matching the backend: every /integration-settings route calls assertAdmin,
  // so a non-admin would only reach a screen that 403s on load.
  { page: 'tenable-integration', labelKey: 'Sidebar.IntegrationSettings', href: '/configuration/integrations', permissionPage: 'Integration Settings', adminOnly: true },
  // Every Excel data sheet in one list. Gated on 'Settings' for the menu; each row
  // then hides itself unless the caller has rights on that sheet's own page.
  { page: 'data-sheets', labelKey: 'Sidebar.DataSheets', href: '/configuration/data-sheets', permissionPage: 'Data Sheets' },
];

/** Policy subsection (categories + integration settings) — same tab styling as configuration.
 *  DEAD: the /policy routes were removed from main.tsx, so these hrefs resolve to nothing.
 *  Only PolicyCategories/PolicySettings still import this, and neither is routed any more. */
export const POLICY_SECTION_NAV: ConfigurationNavItem[] = [
  { page: 'policy-categories', labelKey: 'Sidebar.PolicyCategories', href: '/policy/categories' },
  { page: 'policy-settings', labelKey: 'Sidebar.Settings', href: '/policy/settings' },
];

/** Same logic as Sidebar child links: exact match or path under href */
export function isConfigurationNavItemActive(pathname: string, href: string): boolean {
  if (pathname === href) return true;
  if (href === '/' || href === '#') return false;
  return pathname.startsWith(`${href}/`);
}
